/**
 * Nutrition Tracker Types
 *
 * Types for food logging, daily summaries, and FatSecret API integration.
 */

export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack';

export interface NutritionEntry {
  id: string;                    // auto-generated UUID
  userId: string;
  date: string;                  // YYYY-MM-DD
  mealType: MealType;
  foodName: string;
  brandName?: string;
  servingSize: number;           // e.g. 1.5
  servingUnit: string;           // e.g. "cup", "oz", "g"
  servingId?: string;            // FatSecret serving_id for re-lookup
  calories: number;
  protein: number;               // grams
  carbs: number;                 // grams
  fat: number;                   // grams
  fiber?: number;                // grams
  sugar?: number;                // grams
  fatSecretFoodId?: string;      // For re-lookup
  barcode?: string;              // UPC/EAN if scanned
  isQuickAdd: boolean;           // true = manual entry, false = from search
  syncStatus: 'local' | 'synced' | 'pending';
  createdAt: Date;
  updatedAt: Date;
}

export interface DailyNutritionSummary {
  date: string;
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFat: number;
  totalFiber: number;
  totalSugar: number;
  entries: NutritionEntry[];
  goalCalories: number;
  goalProtein: number;
  goalCarbs: number;
  goalFat: number;
  goalFiber: number;
  goalSugar: number;
}

export interface NutritionGoals {
  calories: number;              // default: 2000
  protein: number;               // default: 150g
  carbs: number;                 // default: 250g
  fat: number;                   // default: 65g
  fiber?: number;                // default: 30g
  sugar?: number;                // default: 50g
}

export const DEFAULT_NUTRITION_GOALS: NutritionGoals = {
  calories: 2000,
  protein: 150,
  carbs: 250,
  fat: 65,
  fiber: 30,
  sugar: 50,
};

export interface FatSecretFoodImage {
  image_url: string;
  image_type: 'Standard' | 'Isolated';
}

export interface FatSecretAllergens {
  is_egg?: number;        // 1 = contains, 0 = free, -1 = unknown
  is_fish?: number;
  is_gluten_free?: number;
  is_lactose?: number;
  is_milk?: number;
  is_nut?: number;
  is_peanut?: number;
  is_sesame?: number;
  is_shellfish?: number;
  is_soy?: number;
}

export interface FatSecretDietaryPrefs {
  is_vegan?: number;       // 1 = yes, 0 = no, -1 = unknown
  is_vegetarian?: number;
}

export interface FatSecretFood {
  food_id: string;
  food_name: string;
  brand_name?: string;
  food_type: 'Generic' | 'Brand';
  food_url: string;
  servings: FatSecretServing[];
  // Premier fields
  food_images?: FatSecretFoodImage[];
  allergens?: FatSecretAllergens;
  dietary_preferences?: FatSecretDietaryPrefs;
  food_sub_categories?: string[];
}

export interface FatSecretServing {
  serving_id: string;
  serving_description: string;
  metric_serving_amount?: string;
  metric_serving_unit?: string;
  is_default?: number;           // Premier: 1 = default serving
  calories: string;
  protein: string;
  carbohydrate: string;
  fat: string;
  fiber?: string;
  sugar?: string;
  saturated_fat?: string;
  sodium?: string;
  cholesterol?: string;
  potassium?: string;
  trans_fat?: string;
  added_sugars?: string;
  vitamin_d?: string;
  vitamin_a?: string;
  vitamin_c?: string;
  calcium?: string;
  iron?: string;
}

// ============================================================================
// CUSTOM MEALS (Saved Recipes)
// ============================================================================

export interface CustomMealItem {
  foodName: string;
  brandName?: string;
  servingSize: number;
  servingUnit: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fatSecretFoodId?: string;
}

// ============================================================================
// GOAL TRACKING & ANALYSIS
// ============================================================================

export type GoalHitStatus = 'hit' | 'over' | 'under';

export interface DailyNutritionGoalStatus {
  date: string;
  caloriesStatus: GoalHitStatus;
  proteinStatus: GoalHitStatus;
  carbsStatus: GoalHitStatus;
  fatStatus: GoalHitStatus;
  fiberStatus: GoalHitStatus;
  sugarStatus: GoalHitStatus;
  allGoalsHit: boolean;
  deltas: {
    calories: number;   // actual - goal
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    sugar: number;
  };
  calorieAdherencePct: number;  // actual / goal * 100
}

export interface WeeklyNutritionSummary {
  startDate: string;   // Monday YYYY-MM-DD
  endDate: string;     // Sunday YYYY-MM-DD
  days: (DailyNutritionGoalStatus | null)[];  // 7 elements, null = no data
  daysTracked: number;
  daysCaloriesHit: number;
  daysProteinHit: number;
  daysAllGoalsHit: number;
  avgCalorieAdherence: number;  // average of calorieAdherencePct across tracked days
}

export interface NutritionAnalysisResult {
  avgCalorieAdherence: number;    // 0-200+, 100 = perfect
  avgProteinGrams: number;
  proteinPerLbBodyweight: number | null;  // null if no bodyweight available
  avgCarbGrams: number;
  avgFatGrams: number;
  streak: number;                 // consecutive days with allGoalsHit
  daysAnalyzed: number;
  daysTracked: number;
  avgCalorieBalance: number;      // avg (actual - goal), negative = deficit
}

// ============================================================================
// CUSTOM MEALS (Saved Recipes)
// ============================================================================

export interface CustomMeal {
  id: string;              // meal_<timestamp>_<random>
  userId: string;
  name: string;            // e.g. "Egg & Egg White Mix"
  items: CustomMealItem[];
  totalCalories: number;
  totalProtein: number;
  totalCarbs: number;
  totalFat: number;
  syncStatus: 'local' | 'synced' | 'pending';
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// FATSECRET RECIPES (from recipes.search.v3)
// ============================================================================

// ============================================================================
// AI-PARSED FOODS (NLP + Image Recognition)
// ============================================================================

export interface ParsedFood {
  food_id: string;
  food_entry_name: string;        // What the user described (e.g., "a bowl of oatmeal")
  eaten: {
    calories: number;
    protein: number;
    carbohydrate: number;
    fat: number;
    fiber: number;
    sugar: number;
    total_weight_g: number;
  } | null;
  suggested_serving: {
    serving_id: string;
    serving_description: string;
    number_of_units: number;
    metric_serving_amount?: string;
    metric_serving_unit?: string;
  } | null;
  food: any | null;               // Full food object if include_food_data was true
}

export interface FatSecretRecipe {
  recipe_id: string;
  recipe_name: string;
  recipe_description: string;
  recipe_image?: string;
  recipe_url?: string;
  preparation_time_min?: number;
  cooking_time_min?: number;
  number_of_servings?: string;
  recipe_nutrition?: {
    calories?: string;
    protein?: string;
    carbohydrate?: string;
    fat?: string;
    fiber?: string;
    sugar?: string;
  };
  ingredients?: { ingredient: string[] | string };
}
